import { AppendReason, ErrorMessage, MRealObject, RemoveReason, StringPropertyTextBox, ViewUpdate, VList, VObject, VObjectInList } from "@1oo4/miv";
import { MArticle, MChapter, MContent, MDocLevel, MSection } from "../../shared/model/mdoc";
import { VRootEdit } from ".";
import { handleError, runAsync } from "../../shared/pages";
import { MRootDoc } from "../../shared/model/masterdata";
import { VContentEdit } from "./contents";

abstract class VDocLevel<M extends MDocLevel<P>, P extends MRealObject, PV extends VObject> extends VObjectInList<M, PV> {
    readonly view: HTMLDivElement;
    readonly name: StringPropertyTextBox;
    private readonly numberIndexing: HTMLSpanElement;

    constructor(m: M, p: PV, tplName: string) {
        super(m, p);

        const template = document.getElementById(tplName) as HTMLTemplateElement
        this.view = (template.content.cloneNode(true) as DocumentFragment).children[0] as HTMLDivElement;

        this.numberIndexing = this.view.querySelector(".number_indexing") as HTMLSpanElement;

        this.name = new StringPropertyTextBox(this.view.querySelector(".edit_name") as HTMLInputElement);
        this.name.Setup(m.Name, this);
        this.name.ErrorHandler = (msg) => handleError(msg);

        const bDelete = this.view.querySelector(".edit_delete") as HTMLButtonElement;
        bDelete.onclick = async () => {
            if (window.confirm("do you want to delete this element and its content ?")) {
                const error: ErrorMessage | undefined = await runAsync(this.Model.DeleteAsync());
                handleError(error);
            };
        }

        const bUp = this.view.querySelector(".edit_up") as HTMLButtonElement;
        bUp.onclick = async () => {
            const error: ErrorMessage | undefined = await runAsync(this.Model.MoveUpAsync());
            handleError(error);
        }
        const bDown = this.view.querySelector(".edit_down") as HTMLButtonElement;
        bDown.onclick = async () => {
            const error: ErrorMessage | undefined = await runAsync(this.Model.MoveDownAsync());
            handleError(error);
        }
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.Model.NumberIndexing)) {
            this.numberIndexing.textContent = this.Model.NumberIndexing.Value.toString();
        }
        this.DocLevelUpdated(vu);
    }
    protected DocLevelUpdated(vu: ViewUpdate): void { }
}

abstract class VDocLevelNotLeaf<M extends MDocLevel<P>, P extends MRealObject, PV extends VObject> extends VDocLevel<M, P, PV> {

    readonly childrenPanel: HTMLDivElement;
    private readonly arrow: HTMLSpanElement;

    constructor(m: M, p: PV, tplName: string) {
        super(m, p, tplName);
        this.childrenPanel = this.view.querySelector(".edit_doclevel_content") as HTMLDivElement;
        this.arrow = this.view.querySelector(".edit_treeview_arrow") as HTMLSpanElement;
        this.arrow.onclick = () => {
            if (this.childrenPanel.style.display == "none") this.expand();
            else this.collapse();
        }

        const bAddSub = this.view.querySelector(".edit_add_sublevel") as HTMLButtonElement;
        bAddSub.onclick = () => {
            this.addSubLevel();
        };
    }

    protected DocLevelUpdated(vu: ViewUpdate): void {
        if (this.hasChildren()) {
            this.arrow.style.color = "black";
        } else {
            this.arrow.style.color = "gray";
            this.collapse();
        }
    }

    abstract addSubLevel(): void;
    abstract hasChildren(): boolean;

    collapse(): void {
        this.arrow.textContent = "▶";
        this.childrenPanel.style.display = "none";
    }
    expand(): void {
        this.arrow.textContent = "▼";
        this.childrenPanel.style.display = '';
    }
}

export class VChapter extends VDocLevelNotLeaf<MChapter, MRootDoc, VRootEdit> {

    readonly articles: VList<VArticle, MArticle>;

    constructor(m: MChapter, parent: VRootEdit) {
        super(m, parent, "tpl_edit_chapter");
        this.articles = new VList<VArticle, MArticle>(m.Articles, this, x => new VArticle(x, this));
        this.collapse();
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.insertBefore(this.view, this.Parent.page.panelChapters.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.page.panelChapters.removeChild(this.view);
    }

    async addSubLevel() {
        const created = await runAsync(this.Model.AppendArticleAsync());
        if (created === undefined) {
            handleError(this.Model.Client.Error, true);
        } else {
            this.expand();
        }
    }
    hasChildren(): boolean {
        return this.articles.Count > 0;
    }
}

class VArticle extends VDocLevelNotLeaf<MArticle, MChapter, VChapter> {

    readonly sections: VList<VSectionEdit, MSection>;

    constructor(m: MArticle, parent: VChapter) {
        super(m, parent, "tpl_edit_article");
        this.sections = new VList(m.Sections, this, x => new VSectionEdit(x, this));
        this.collapse();
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.childrenPanel.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.childrenPanel.insertBefore(this.view, this.Parent.childrenPanel.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.childrenPanel.removeChild(this.view);
    }


    async addSubLevel() {
        const created = await runAsync(this.Model.AppendSectionAsync());
        if (created === undefined) {
            handleError(this.Model.Client.Error, true);
        } else {
            this.expand();
        }
    }
    hasChildren(): boolean {
        return this.sections.Count > 0;
    }
}

export class VSectionEdit extends VDocLevel<MSection, MArticle, VArticle> {

    readonly panelContent: HTMLDivElement;
    private readonly contents: VList<VContentEdit, MContent>;
    private _loaded_doc_: boolean = false;

    constructor(m: MSection, parent: VArticle) {
        super(m, parent, "tpl_edit_section");
        this.panelContent = document.createElement("div");
        this.panelContent.classList.add("stackpanel");
        this.name.HTMLInput.onfocus = () => { this.Parent.Parent.Parent.page.selectedCSection = this; }
        this.contents = new VList(m.Contents, this, x => x.makeViewEdit(this));
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.childrenPanel.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.childrenPanel.insertBefore(this.view, this.Parent.childrenPanel.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.childrenPanel.removeChild(this.view);
        if (rr != RemoveReason.TEMPORARY) {
            const page = this.Parent.Parent.Parent.page;
            if (page.selectedCSection == this) {
                page.selectedCSection = undefined;
            }
        }
    }

    setSelected(s: boolean) {
        if (s) {
            this.view.style.background = "blue";
            if (!this._loaded_doc_) {
                this._loaded_doc_ = true;
                runAsync(this.Model.LoadContentsAndUpdateUIAsync());
            }
        } else {
            this.view.style.background = "grey";
        }
    }
}